"use client";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Portal,
  Slide,
  Stack,
  Tooltip,
} from "@mui/material";
import type { Table as TanstackTable } from "@tanstack/react-table";
import type * as React from "react";
import { createContext, useContext } from "react";

export type SelectionAction<TData> = {
  /** Unique key for React */
  key: string;
  label: string;
  title: string; // For tooltip, if different from label
  icon?: React.ReactNode;
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success" | "inherit";
  variant?: "contained" | "outlined" | "text";
  /** Called with the currently selected rows' original data */
  onClick: (selectedRows: TData[]) => void;
  /** Optionally disable when certain rows are selected */
  disabled?: (selectedRows: TData[]) => boolean;
};

type DataTableSelectionBarContextValue = {
  table: TanstackTable<unknown>;
  selectedData: unknown[];
};

const DataTableSelectionBarContext = createContext<DataTableSelectionBarContextValue | null>(null);

export function useDataTableSelectionBarContext<TData>(): {
  table: TanstackTable<TData>;
  selectedData: TData[];
} {
  const ctx = useContext(DataTableSelectionBarContext);
  if (!ctx) {
    throw new Error("useDataTableSelectionBarContext must be used within DataTableSelectionBar");
  }
  return {
    table: ctx.table as TanstackTable<TData>,
    selectedData: ctx.selectedData as TData[],
  };
}

type DataTableSelectionBarProps<TData> = {
  table: TanstackTable<TData>;
  /** Built-in action buttons; ignored when `children` is provided */
  actions?: SelectionAction<TData>[];
  /** Custom action buttons (e.g. per-feature components with their own mutations) */
  children?: React.ReactNode;
  /** Label shown in the chip, e.g. "delivery" → "3 deliveries selected" */
  entityLabel?: string;
};

export function DataTableSelectionBar<TData>({
  table,
  actions = [],
  children,
  entityLabel = "row",
}: DataTableSelectionBarProps<TData>) {
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedCount = selectedRows.length;
  const selectedData = selectedRows.map((r) => r.original);

  const contextValue: DataTableSelectionBarContextValue = {
    table: table as TanstackTable<unknown>,
    selectedData,
  };

  return (
    <Portal>
      <DataTableSelectionBarContext.Provider value={contextValue}>
        <Slide direction="up" in={selectedCount > 0} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              bottom: 30,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              zIndex: 1300,
              pointerEvents: "none",
            }}
          >
            <Paper
              elevation={8}
              sx={{
                pointerEvents: "auto",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "primary.main",
                bgcolor: "background.paper",
                px: 2,
                py: 1.25,
                minWidth: 320,
                maxWidth: "calc(100% - 48px)",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                {/* Count chip */}
                <Chip
                  label={`${selectedCount} ${entityLabel}${selectedCount !== 1 ? "s" : ""} selected`}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />

                <Divider orientation="vertical" flexItem />

                {/* Action buttons */}
                <Stack direction="row" gap={1} flexWrap="wrap" flex={1}>
                  {children ??
                    actions.map((action) => (
                      <Tooltip key={action.key} title={action.title ?? action.label}>
                        <span>
                          <Button
                            size="small"
                            variant={action.variant ?? "outlined"}
                            color={action.color ?? "primary"}
                            startIcon={action.icon}
                            disabled={action.disabled?.(selectedData as TData[]) ?? false}
                            onClick={() => action.onClick(selectedData as TData[])}
                          >
                            {action.label}
                          </Button>
                        </span>
                      </Tooltip>
                    ))}
                </Stack>

                {/* Dismiss */}
                <IconButton
                  size="small"
                  onClick={() => table.resetRowSelection()}
                  aria-label="clear selection"
                  sx={{ ml: "auto" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          </Box>
        </Slide>
      </DataTableSelectionBarContext.Provider>
    </Portal>
  );
}
